// @ts-check
import Vue from 'vue';
import { getItemHashKey, saveState } from './helpers';
import { state } from '../state';
import { getComboStake } from './stake';
import { resetFolds } from './folds';
import { calculateBonuses, calculateSingleBonus } from './calculations';
import { resetQuickbet } from './quickbet';
import { configStore } from './config.store';
import { BETSLIP_VALIDATION_ERRORS, mapBetbuilderFixture, ITEM_TYPES } from 'bto-models/betslip';
import BetBuilderOddRequest from '@bto-services/api/shared/betbuilder-odd.request';
import SwishBetRequest from '@bto-services/api/shared/swish-bet-request-service';
import SwishBetBuilderRequestModel from 'bto-models/betslip/betbuilder/swish-betbuilder-request-model'
import { setToastNotification } from '@bto-services/toast-notification';
import { getToastAdditionalTime, TOAST_NOTIFICATION_MESSAGE_STATUS } from 'bto-models/toast-notification';
import { showBetslipValidation } from '@bto-services/modal';
import { removeAlgoItem } from './algo';

/**
 * @typedef {import('bto-models/betslip/interfaces').BetslipItem} BetslipItem
 * @typedef {import('bto-models/betslip/interfaces').BetslipBetBuilderMarket} BetslipBetBuilderMarket
 * @typedef {import('bto-models/betslip/interfaces').BetBuilderFixture} BetBuilderFixture
 * @typedef {import('bto-models/betslip/interfaces').BetslipItemKey} BetslipItemKey
 * @typedef {import('bto-models/betslip/interfaces').BetslipMatchItem} BetslipMatchItem
 */

let swishRequestDelay = {};
let requestOddInProcess = false;

/**
 * Toggle races aditional odds from betslip
 * @param {BetslipItemKey} item - betslip item
 */
export const toggleRaceOdds = (item) => {
	const itemKey = getItemHashKey(item);
	const lastComboStake = getComboStake();
	if (state.raceOdds[itemKey]) {
		Vue.delete(state.raceOdds, itemKey);
	} else {
		Vue.set(state.raceOdds, itemKey, true);
	}
	resetFolds(lastComboStake);
	calculateSingleBonus(itemKey);
	calculateBonuses();
	saveState();
};

/**
 * Toggle banker flag from betslip
 * @param {BetslipItemKey} item - betslip item
 */
export const toggleBanker = (item) => {
	const itemKey = getItemHashKey(item);
	const lastComboStake = getComboStake();
	if (state.bankers[itemKey]) {
		Vue.delete(state.bankers, itemKey);
		Vue.set(state.matchItems[item.matchId], 'hasBankers', false);
	} else {
		Vue.set(state.bankers, itemKey, true);
		Vue.set(state.matchItems[item.matchId], 'hasBankers', true);
	}
	resetFolds(lastComboStake);
	calculateBonuses();
	saveState();
};

/**
 * Validate if we can add item
 * @param {BetslipItem} item - betslip item
 */
export const validateItem = (item) => {
	configStore.settings.MULTIPLE_SAME_MATCH_MARKETS
	if (configStore.settings.MULTIPLE_SAME_MATCH_MARKETS && state.matchItems[item.matchId]) {
		return BETSLIP_VALIDATION_ERRORS.SAME_MATCH;
	}
	if (Object.keys(state.items).length >= (configStore.settings.MAX_ITEMS || Infinity)) {
		return BETSLIP_VALIDATION_ERRORS.MAX_ITEMS;
	}
	if (configStore.settings.NO_BOOSTED_IN_FREEBETS && (item.itemType === ITEM_TYPES.BOOSTED_ODD) && state.freebetId) {
		return BETSLIP_VALIDATION_ERRORS.BOOSTED_IN_FREEBET;
	}
	if (checkBoostedSameEvent(item)) {
		return BETSLIP_VALIDATION_ERRORS.BOOSTED_SAME_MATCH;
	}
	if (configStore.settings.NO_MULTIPLE_RACE_MATCH && checkRaceSameEvent(item)) {
		return BETSLIP_VALIDATION_ERRORS.SAME_MATCH;
	}
	return 0;
};

/**
 * Remove single item from betslip
 * @param {BetslipItemKey} item - betslip item
 */
export const removeItem = (item) => {
	const itemKey = getItemHashKey(item);
	const lastComboStake = getComboStake();
	Vue.delete(state.items, itemKey);
	if (state.matchItems[item.matchId]) {
		state.matchItems[item.matchId].totalBetItems -= 1;
		if (state.matchItems[item.matchId].totalBetItems <= 0) {
			Vue.delete(state.matchItems, item.matchId);
		}
	}
	if (state.singles[itemKey]) {
		Vue.delete(state.singles, itemKey);
	}
	if (state.singleBonuses[itemKey]) {
		Vue.delete(state.singleBonuses, itemKey);
	}
	if (state.bankers[itemKey]) {
		Vue.delete(state.bankers, itemKey);
	}
	if (state.raceOdds[itemKey]) {
		Vue.delete(state.raceOdds, itemKey);
	}
	if (state.algoItems[item.matchId] && !state.algoItems[item.matchId].isActive) {
		removeAlgoItem(item);
	}
	if (state.algoItems[item.matchId] && (itemKey === getItemHashKey(state.algoItems[item.matchId].algoItem) || state.algoItems[item.matchId].singles.length === 0)) {
		Vue.delete(state.algoItems, item.matchId);
	}

	if (state.bookedBetslipDetails.bookCode && Object.keys(state.matchItems).length === 0) {
		state.bookedBetslipDetails.bookCode = null;
		state.bookedBetslipDetails.matchItems = [];
	}
	resetFolds(lastComboStake);
	calculateBonuses();
	saveState();
};

/**
 * Add item to betslip
 * @param {BetslipItem} item - betslip item
 * @param {BetslipMatchItem} match - betslip match item
 */
export const addItem = (item, match) => {
	const error = validateItem(item);
	if (error) {
		return error;
	}
	const itemKey = getItemHashKey(item);
	const lastComboStake = getComboStake();
	// If match item doesn't exist for this match add it into match item map
	if (!state.matchItems[item.matchId]) {
		Vue.set(state.matchItems, item.matchId, { ...match, totalBetItems: 0 });
	}
	// Update the total items for given match
	state.matchItems[item.matchId].totalBetItems += 1;

	Vue.set(state.items, itemKey, item);
	resetFolds(lastComboStake);
	calculateBonuses();
	saveState();
};

/**
 * Toggle item from betslip
 * @param {BetslipItem} item - betslip item
 * @param {BetslipMatchItem} match - betslip match item
 */
export const toggleItem = (item, match) => {
	const itemKey = getItemHashKey(item);
	// If item exists remove it
	if (state.items[itemKey]) {
		removeItem(item);
		return;
	}
	// Otherwise add it to betslip
	return addItem(item, match);
};

/**
 * Toggle Swish bet builder item from betslip
 * @param {BetBuilderFixture} fixture - betbuilder fixture
 * @param {BetslipBetBuilderMarket & {isComplex: boolean}} market - betbuilder market
 */
export async function toggleSwishBetBuilder(fixture, market) {
	resetQuickbet();
	if (requestOddInProcess) return;
	const marketId = configStore.settings.BET_BUILDER_SWISH_MARKET_ID;
	const outcomeId = configStore.settings.BET_BUILDER_SWISH_OUTCOME_ID;
	const betItemKey = { marketId, outcomeId, matchId: fixture.matchId };
	const itemKey = getItemHashKey(betItemKey);
	const betItem = state.items[itemKey];
	const { betslipItem, betslipMatch } = mapBetbuilderFixture(fixture, marketId, outcomeId);
	if (betItem) {
		const betBuilderMarketIsSelected = !!betItem.betBuilder[market.globalId];
		if (betBuilderMarketIsSelected) {
			return removeBetBuilderItem(betItemKey, market);
		}
		return swishBetBuilderRequest(betslipItem, market, false);
	}
	addItem({ ...betslipItem, odd: "1" }, betslipMatch);
	addBetBuilderMarket(market, itemKey, true);
	calculateSingleBonus(itemKey);
	calculateBonuses();
	saveState();
	return false;
}
/**
 * Toggle bet builder item from betslip
 * @param {BetBuilderFixture} fixture - betbuilder fixture
 * @param {BetslipBetBuilderMarket & {isComplex: boolean}} market - betbuilder market
 */
export async function toggleBetBuilder(fixture, market) {
	resetQuickbet();
	const marketId = configStore.settings.BET_BUILDER_MARKET_ID;
	const outcomeId = configStore.settings.BET_BUILDER_OUTCOME_ID;
	const betItemKey = { marketId, outcomeId, matchId: fixture.matchId };
	const itemKey = getItemHashKey(betItemKey);
	const { isComplex, ...betBuilderMarket } = market;
	const betItem = state.items[itemKey];
	if (betItem) {
		const betBuilderMarketIsSelected = !!betItem.betBuilder[market.globalId];
		if (!isComplex && betBuilderMarketIsSelected) {
			// remove betbuilder market
			removeBetBuilderItem(betItemKey, market);
			return true;
		}
		// If complex market and already selected do noting
		if (betBuilderMarketIsSelected) {
			const expireTime = Date.now() + getToastAdditionalTime();
			const key = `${APP_SETTINGS.player.id}_betbuilder_success`;
			setToastNotification(key, {
				playerId: APP_SETTINGS.player.id,
				expireTime,
				message: 'BB.AlreadyAddedSelection',
				status: TOAST_NOTIFICATION_MESSAGE_STATUS.SUCCESS,
			});
			return true;
		}
		// Add betbuilder market if sport cast returs valid price for it
		// Omit it otherwise
		return getBetBuilderPrice(betItem, betBuilderMarket);
	}
	const { betslipItem, betslipMatch } = mapBetbuilderFixture(fixture, marketId, outcomeId);
	const error = addItem(betslipItem, betslipMatch);
	if (error) {
		showBetslipValidation(error);
		return true;
	}
	addBetBuilderMarket(betBuilderMarket, itemKey);
	return true;
}

/**
 * Add betbuilder market to betslip item
 * @param {BetslipBetBuilderMarket} market - betbuilder market
 * @param {string} itemKey - betslip item key
 * @param {boolean} [alreadyTranslated] - indicates if the description has been already translated
 */
function addBetBuilderMarket(market, itemKey, alreadyTranslated = false) {
	// If market is not translated, call the get price method in order to get the transalation
	// This is not translated only on first betbuilder market addition in betslip
	if (!alreadyTranslated) {
		getBetbuilderRequest(state.items[itemKey], market).execute().then((resp) => {
			if (resp.data.Error || !resp.data.PayLoad || !resp.data.PayLoad.BetResponse || !resp.data.PayLoad.BetResponse.Translations) {
				return;
			}
			const description = getBetBuilderDescriptionTranslation(resp.data.PayLoad.BetResponse.Translations);
			if (description) {
				market.description = description;
				saveState();
			}
		});
	}
	Vue.set(state.items[itemKey].betBuilder, market.globalId, market);
	const expireTime = Date.now() + getToastAdditionalTime();
	const key = `${APP_SETTINGS.player.id}_betbuilder_success`;
	setToastNotification(key, {
		playerId: APP_SETTINGS.player.id,
		expireTime,
		message: 'BB.AddedSelection',
		status: TOAST_NOTIFICATION_MESSAGE_STATUS.SUCCESS,
	});
	saveState();
}

/**
 * Remove betbuilder market from betslip
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} market - Bet Builder market
 */
export function removeBetBuilderItem(item, market) {
	const itemKey = getItemHashKey(item);
	Vue.delete(state.items[itemKey].betBuilder, market.globalId);

	// If we removed all bet builder markets remove betslip item from betslip
	if (Object.keys(state.items[itemKey].betBuilder).length === 0) {
		removeItem(item);
		return;
	}
	if ('matchPlayerId' in market || 'preMatchMarketId' in market) {
		if (swishRequestDelay[itemKey]) {
			clearTimeout(swishRequestDelay[itemKey]);
			delete swishRequestDelay[itemKey];
		}
		if (Object.keys(state.items[itemKey].betBuilder).length === 1) {
			changeItemOdd(item, "1")
			return false;
		}
		swishRequestDelay[itemKey] = setTimeout(() => {
			swishBetBuilderRequest(item, null, true);
		}, 300);
		return true;
	}
	getBetBuilderPrice(item);
}

/**
 * Remove betbuilder market from betslip
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} [market] - Additional Bet Builder market
 */
async function getBetBuilderPrice(item, market) {
	const itemKey = getItemHashKey(item);
	let betBuilderMarketCount = Object.keys(state.items[itemKey].betBuilder).length;
	if (market) {
		betBuilderMarketCount += 1;
	}
	// If we have single market set the odd to 1 and return
	if (betBuilderMarketCount === 1) {
		changeItemOdd(item, '1');
		return true;
	}
	// If we have more than 1 market get the price from SportCast API
	return fetchBetBuilderOdd(item, market);
}

/**
 * Get filled bet builder odd request for given bet builder item
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} [market] - Additional Bet Builder market
 * @param {boolean} [save] - Flag to save bet builder selection on sportCast
 */
export function getBetbuilderRequest(item, market, save = false) {
	const itemKey = getItemHashKey(item);
	const betItem = state.items[itemKey];
	const matchItem = state.matchItems[item.matchId];
	let betBuilderOddRequest = new BetBuilderOddRequest();
	betBuilderOddRequest.baseUrl = configStore.settings.SPORTCAST_API_URL;
	betBuilderOddRequest.postParams.key = configStore.settings.BET_BUILDER_API_KEY;
	betBuilderOddRequest.postParams.fixtureId = matchItem.sportCastFixtureId;
	betBuilderOddRequest.postParams.sport = matchItem.sportCastSportId;
	betBuilderOddRequest.postParams.bets = Object.keys(betItem.betBuilder);

	if (market) {
		betBuilderOddRequest.postParams.bets.push(market.globalId);
	}
	if (save) {
		betBuilderOddRequest.postParams.saveBetSlip = true;
		betBuilderOddRequest.postParams.returnBetResponse = true;
	}

	return betBuilderOddRequest;
}
/**
 * Get filled bet builder odd request for given bet builder item
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} [market] - Additional Bet Builder market
 * @param {boolean} [itsDelete] - Flag that indicates if we are deleting Bet Builder market
 */
export function getSwishBetBuilderRequest(item, market, itsDelete = false) {
	const itemKey = getItemHashKey(item);
	const swishLongIds = Object.values(state.items[itemKey].betBuilder)
	if (market !== undefined && !itsDelete) swishLongIds.push(market);
	const swishBetRequest = new SwishBetRequest();
	const betMatch = state.matchItems[item.matchId];
	const requestModel = new SwishBetBuilderRequestModel(swishLongIds, betMatch);
	swishBetRequest.postParams = requestModel;
	return swishBetRequest;
}

/**
 * Get filled bet builder odd request for given Swish bet builder item
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} [market] - Additional Bet Builder market
 * @param {boolean} [itsDelete] - Additional Bet Builder market
 */
export function swishBetBuilderRequest(item, market, itsDelete) {
	const itemKey = getItemHashKey(item);
	const betItem = state.items[itemKey];
	if (swishRequestDelay[itemKey]) {
		clearTimeout(swishRequestDelay[itemKey]);
		delete swishRequestDelay[itemKey];
	}
	// requestOddInProcess = true;
	const betRequest = getSwishBetBuilderRequest(item, market, itsDelete);
	return betRequest.execute().then(resp => {
		requestOddInProcess = false;
		if (resp.data.o === 0) {
			const expireTime = Date.now() + getToastAdditionalTime();
			const key = `${APP_SETTINGS.player.id}_betbuilder_invalid`;
			setToastNotification(key, {
				playerId: APP_SETTINGS.player.id,
				expireTime,
				message: resp.data.e || 'BB.IncompatibleSelection',
				status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
			});
			return false;
		}
		// If validation passed and we have new market add it to betslip item
		if (market) {
			addBetBuilderMarket(market, itemKey, true);
		}
		// Change new odd from response
		const newOdd = `${resp.data.o}`;
		changeItemOdd(betItem, newOdd);
		return false;
	});
}

/**
 * 
 * @param {BetslipItemKey} item - Betslip item
 * @param {BetslipBetBuilderMarket} [market] - Additional Bet Builder market
 */
function fetchBetBuilderOdd(item, market) {
	const betBuilderOddRequest = getBetbuilderRequest(item, market);
	const itemKey = getItemHashKey(item);
	const betItem = state.items[itemKey];

	return betBuilderOddRequest.execute().then(resp => {
		if (resp.data.Error || !resp.data.PayLoad || resp.data.PayLoad.Price === 'Infinity' || resp.data.PayLoad.Price === 1) {
			const expireTime = Date.now() + getToastAdditionalTime();
			const key = `${APP_SETTINGS.player.id}_betbuilder_invalid`;
			setToastNotification(key, {
				playerId: APP_SETTINGS.player.id,
				expireTime,
				message: 'BB.IncompatibleSelection',
				status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
			});
			return false;
		}
		// If validation passed and we have new market add it to betslip item
		if (market) {
			const description = getBetBuilderDescriptionTranslation(resp.data.PayLoad.BetResponse.Translations);
			if (description) {
				market.description = description;
			}
			addBetBuilderMarket(market, itemKey, true);
		}
		// Change new odd from response
		const newOdd = `${resp.data.PayLoad.Price}`;
		changeItemOdd(betItem, newOdd);
		return true;
	});
}

/**
 * Reset betslip upDown class after animation ends
 * @param {BetslipItem} item - betslip item
 */
export function resetUpDown(item) {
	const itemKey = getItemHashKey(item);
	Vue.delete(state.items[itemKey], 'upDown');
	saveState();
}

/**
 * Change odd for item
 * @param {BetslipItemKey} item - betslip item
 * @param {string} newOdd - new odd for item
 * @param {boolean} [isBetStop] - flag if it's betstop
 */
export function changeItemOdd(item, newOdd, isBetStop) {
	const itemKey = getItemHashKey(item);
	const oldItem = state.items[itemKey];
	const oddDiff = (+oldItem.odd) - (+newOdd);
	const upDown = oddDiff < 0 ? 1 : oddDiff === 0 ? 0 : -1;
	if (isBetStop) {
		oldItem.isLocked = true;
	} else {
		delete oldItem.isLocked;
	}
	Vue.set(state.items, itemKey, { ...oldItem, odd: newOdd, upDown });
	calculateSingleBonus(itemKey);
	calculateBonuses();
	saveState();
}
/**
 * Change odd for item
 * @param {BetslipItemKey} item - betslip item
 * @param {string} newOdd - new odd for item
 * @param {string} marketId - new market id
 * @param {string} newOutcomeId - new outcome ids
 * @param {string} identifier -  identifier for live match
 * @param {string} message -  info message for item 
 */

export function changeTransitionToLiveItem(item, newOdd, marketId,newOutcomeId, identifier, message) {
	const itemKey = getItemHashKey(item);
	const oldItem = state.items[itemKey];
	const oddDiff = (+oldItem.odd) - (+newOdd);
	const upDown = oddDiff < 0 ? 1 : oddDiff === 0 ? 0 : -1;
	const algoItem = state.algoItems[item.matchId] ? state.algoItems[item.matchId].singles[0] : {}
	// delete old item key and item
	Vue.delete(state.items, itemKey);
	Vue.delete(state.singleBonuses, itemKey);
	Vue.delete(state.singles, itemKey);
	if(state.algoItems[item.matchId]){
	Vue.delete(state.algoItems[item.matchId].singles, 0)
	}
 // new itemKey with changed dat
	let newItemkey = `${item.matchId}_${marketId}_${newOutcomeId}`

	Vue.set(state.algoItems, item.matchId,{ singles: [ { ...algoItem, odd:newOdd, marketId:marketId, upDown, message:message, identifier:identifier, outcomeId:newOutcomeId, itemType: ITEM_TYPES.LIVE  }]});
	Vue.set(state.items,  newItemkey, { ...oldItem, odd:newOdd, marketId:marketId, upDown, message:message, identifier:identifier, outcomeId:newOutcomeId, itemType: ITEM_TYPES.LIVE  });
	
	calculateSingleBonus(newItemkey);
	calculateBonuses();
	saveState();
}


/**
 * Translates descriptions
 * @param {{Culture: string,Description:string[]}[]} translations - translations
 */
function getBetBuilderDescriptionTranslation(translations) {
	const culture = window['sportCastCulture'] || APP_SETTINGS.player.language;
	const locales = translations.find(x => x.Culture.startsWith(culture));
	if (!locales) {
		return '';
	}
	return locales.Description.pop();
}

/** ===================================================
 * 	GETTERS SECTION
 * ====================================================
 */

/**
 * Get total number of match items in betslip
 */
export const getTotalMatchItems = () => Object.keys(state.matchItems).length;

/**
 * Check if banker is selected for given match and outcome is different
 * Only 1 banker can be selected per unique match
 * @param {BetslipItemKey} item - bet item
 */
export const checkSameMatchBanker = (item) => !state.bankers[getItemHashKey(item)] && state.matchItems[item.matchId].hasBankers;

/**
 * Check if odd is in betslip
 * @param {BetslipItemKey} item - betslip item
 */
export const isOddActive = (item) => {
	const itemKey = getItemHashKey(item);
	if (state.algoItems[item.matchId] && !state.items[itemKey]) {
		return state.algoItems[item.matchId].singles.some(single => getItemHashKey(single) === itemKey);
	}
	return !!state.items[itemKey];
};


/**
 * Get item hash key for bet builder item
 * @param {BetBuilderFixture} match - match fixture object
 */
export const getBetBuilderItemKey = (match) => getItemHashKey({
	matchId: match.matchId,
	marketId: configStore.settings.BET_BUILDER_MARKET_ID,
	outcomeId: configStore.settings.BET_BUILDER_OUTCOME_ID
});

/**
 * Check if boosted item is connected with same event id(s) in betslip
 * @param {BetslipItem} item - betslip item
 */
export const checkBoostedSameEvent = (item) => {
	// If boosted item is connected to another regular item in matchItems with same event
	if ((item.itemType === ITEM_TYPES.BOOSTED_ODD) && item.eventIds.some(v => state.matchItems[v])) {
		return true;
	}
	// If boosted item is connected to another boosted item in matchItems with same event
	if ((item.itemType === ITEM_TYPES.BOOSTED_ODD) && Object.values(state.matchItems).some((i) => {
		return i.eventIds && i.eventIds.some(ei => item.eventIds.includes(ei));
	})) {
		return true;
	}
	// If regular item is connected to another boosted item in matchItems with same event
	if ((item.itemType !== ITEM_TYPES.BOOSTED_ODD) && Object.values(state.matchItems).some(i => i.eventIds && i.eventIds.includes(item.matchId))) {
		return true;
	}
	return false;
};


export const checkRaceSameEvent = (item) => {
	// If race item is connected to another regular item in matchItems with same event
	if ((item.itemType === ITEM_TYPES.OUTRIGHT) && Object.values(state.items).some(i => i.runnersDetails)) {
		return true;
	}
	// If race item is connected to another race item in matchItems with same event
	if ((item.itemType === ITEM_TYPES.OUTRIGHT) && item.runnersDetails && (Object.keys(state.items).length > 0)) {
		return true
	}
	// If regular item is connected to another race item in matchItems with same event
	if ((item.itemType !== ITEM_TYPES.OUTRIGHT && Object.values(state.items).some(i => i.runnersDetails))) {
		return true;
	}
	return false;
};